/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { DasClient } from '@wit/vtv-x-foundation-lib';
import { DasStatusCode } from 'types/libs/das/DasStatusCode';

// Das client implementation for web
class DasClientWeb implements DasClient {
  private static instance: DasClientWeb;

  private sessionId = '';

  // singleton
  constructor() {
    if (DasClientWeb.instance) {
      return DasClientWeb.instance;
    }
    DasClientWeb.instance = this;
  }

  initSession = async (opVaultUrl: string): Promise<string | null> => {
    const success = await this.setup(opVaultUrl);

    if (success) {
      return this.getChallenge();
    }

    return null;
  };

  updateSession = async (encryptedSessionRight: string): Promise<boolean> => {
    return this.importLicense(encryptedSessionRight);
  };

  signPayload = async (payload: string, keyId: string): Promise<string | null> => {
    return this.sign(payload, keyId);
  };

  private async setup(url: string): Promise<boolean> {
    const initResponse: DasResponse = await init(url);

    if (initResponse.status === DasStatusCode.NV_DAS_SUCCESS) {
      const openSessionResult: DasResponse = await openSession();

      if (openSessionResult.status === DasStatusCode.NV_DAS_SUCCESS) {
        this.sessionId = openSessionResult.data;
        return true;
      }
    }

    return false;
  }

  private async getChallenge(): Promise<string | null> {
    const challenge = await getChallenge(this.sessionId);

    if (challenge.status === DasStatusCode.NV_DAS_SUCCESS) {
      return challenge.data;
    }
    return null;
  }

  private async importLicense(encryptedSessionRight: string): Promise<boolean> {
    const saveLicence: DasResponse = await importLicense(this.sessionId, encryptedSessionRight);
    return saveLicence.status === DasStatusCode.NV_DAS_SUCCESS;
  }

  private async sign(body: string, keyId: string): Promise<string | null> {
    const signedBody: SignResponse = await sign(this.sessionId, keyId, DasClientWeb.dataToBufferStr(body));

    if (signedBody.status === DasStatusCode.NV_DAS_SUCCESS) {
      return DasClientWeb.Uint8ArrayToHexStr(signedBody.data);
    }

    return null;
  }

  // utils
  static dataToBufferStr = (data: string): string => {
    return convertStringtoBuffer(data).data.toString();
  };

  static Uint8ArrayToHexStr = (byteArray: Uint8Array): string => {
    // byteArray with values 0-255
    return byteArray.reduce((output, unsignedByte) => output + unsignedByte.toString(16).padStart(2, '0'), '');
  };
}

const dasClient: DasClient = new DasClientWeb();

export default dasClient;
